import * as React from "react";
import {useRef} from "react";
import {clsx} from "clsx";
import {type LegacySingleDatePickerDrops, useLegacySingleDatePicker} from "@/shared/ui/compatibility/useLegacySingleDatePicker";
import {Temporal} from "@js-temporal/polyfill";

interface DatePickerInputProps {
	id?: string;
	/**
	 * The selected date as a {@link Temporal.PlainDate}, or {@code null} when empty. This is the canonical
	 * date type: formatting to/from {@link globals.dateFormat} happens only inside the legacy bridge,
	 * so consumers never deal with date strings.
	 */
	value: Temporal.PlainDate | null;
	onValueChange: (value: Temporal.PlainDate | null) => void;
	drops?: LegacySingleDatePickerDrops;
	/**
	 * Renders the input non-interactive. A disabled input cannot be focused or clicked, so the legacy
	 * daterangepicker — which opens on focus/click — never appears; no extra teardown is needed.
	 */
	disabled?: boolean;
	/** Extra classes appended to the `<input>` (on top of `form-control`). */
	className?: string;
}

/**
 * Renders the bare text input only. A calendar-icon button is not included — if the caller wants one,
 * it can add its own `<label htmlFor={id}>` (see {@link DateTimeRangePanel} for the established pattern),
 * which focuses this input natively without needing a ref.
 */
export function DatePickerInput({id, value, onValueChange, drops, disabled = false, className}: DatePickerInputProps) {
	const inputRef = useRef<HTMLInputElement>(null);

	useLegacySingleDatePicker({inputRef, value, onValueChange, drops});

	return (
		<input id={id} type="text" ref={inputRef} className={clsx("form-control", className)} maxLength={10} disabled={disabled} />
	);
}

export type {DatePickerInputProps};
